import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BuildingId, FeatureId, FeatureVisibility, LangCode, PermissionId, UserSelf } from 'shared/models';
import { getCurrentUserPermissions, getCurrentUser } from 'selectors/sessionSelectors';
import { getAllBuildingOrgs } from 'selectors/buildingOrgsSelectors';
import { Permission } from 'shared/permissions';
import { getFeatureVisibility } from 'selectors/featuresSelectors';
import { useTranslation } from 'react-i18next';
import { getRoles } from 'selectors/rolesSelectors';
import { values } from 'lodash';

export type Session = {
    user: UserSelf | null;
    langCode: LangCode;
    // HACK: Explicitly separate the first permission from an args array of extra permissions
    //       rather than just have one args array of all permissions to enforce that the caller
    //       is passing at least one permission. There have been too many times where I've put
    //       hasPermission(Permission.AllOrganizations) and forgotten to put the null buildingId.
    //       Note: it seems that the actual funtion implementation is okay to keep the original
    //       definition with just buildingId and permissionIds.
    hasPermission(buildingId: BuildingId | null, firstPermission: PermissionId, ...extraPermissionIds: PermissionId[]): boolean;
    hasFeature(featureId: FeatureId): boolean;
};

const SessionContext = createContext<Session>(undefined as any);

export type Props = {
    children?: ReactNode;
};

export const SessionProvider = ({ children }: Props) => {
    const { i18n } = useTranslation();
    const user = useSelector(getCurrentUser);
    const orgId = user && user.orgId;

    const features = useSelector(getFeatureVisibility);
    const permissions = useSelector(getCurrentUserPermissions);
    const roles = useSelector(getRoles);
    if (user && roles) {
        user.roleIds = values(roles).map(role => role.id);
    }
    const allBuildingOrgs = useSelector(getAllBuildingOrgs);

    const hasPermission = useCallback((buildingId: BuildingId | null, ...permissionIds: PermissionId[]) => {
        if (!permissionIds.length) {
            throw new Error('Invalid permission check'); // Did you add buildingId to your call? e.g. hasPermission(buildingId, Permission.Something);
        }

        // Bail out if the user is missing any of the requested permissions
        if (!permissionIds.every(pid => permissions[pid])) {
            return false;
        }
        // Bail out if the building-org mask is missing any of the requested permissions
        // But bypass this test if the user has AllOrganizations permission, which ignores building-org links
        if (buildingId && orgId && !permissions[Permission.AllOrganizations]) {
            const buildingOrgs = allBuildingOrgs[buildingId] || [];
            const buildingOrg = buildingOrgs.find(bo => bo.orgId === orgId);
            if (buildingOrg) {
                const permissionMask = buildingOrg.permissionMask.split(',').reduce<Record<PermissionId, boolean>>((all, pid) => ({ ...all, [pid]: true }), {});
                if (!permissionIds.every(pid => permissionMask[pid])) {
                    return false;
                }
            }
        }
        return true;
    }, [ permissions, allBuildingOrgs, orgId ]);

    const hasFeature = useCallback((featureId: FeatureId) => {
        const visibility = features[featureId];
        switch (visibility) {
            case FeatureVisibility.VisibleToAll:
                return true;

            case FeatureVisibility.MadAdmins:
                return hasPermission(null, Permission.AllOrganizations);

            case FeatureVisibility.MadAdminsOptIn:
                if (!hasPermission(null, Permission.AllOrganizations)) {
                    return false;
                }

                // M1-1712: HACK: an already-logged-in user won't have optIns... so bail out. (Remove this hack after Prod has been upgraded)
                if (!user?.optIns) return false;

                return !!user?.optIns[featureId];

            case FeatureVisibility.UserOptIn:
                // M1-1712: HACK: an already-logged-in user won't have optIns... so bail out. (Remove this hack after Prod has been upgraded)
                if (!user?.optIns) return false;

                return !!user?.optIns[featureId];

            case FeatureVisibility.Hidden:
            default:
                return false;
        }
    }, [ user, hasPermission ]);

    const currentLangCode =
        i18n.language?.toUpperCase()?.replace("-", "_") as LangCode
        || LangCode.EN_US;

    const session = useMemo(() => ({
        user,
        langCode:
            LangCode[currentLangCode]
                ? currentLangCode
                : LangCode.EN_US,
        hasPermission,
        hasFeature,
    }), [ user, currentLangCode, hasPermission, hasFeature ]);

    return (
        <SessionContext.Provider value={ session }>
            { children }
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    return useContext(SessionContext);
};
